import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { isEmpty } from 'lodash';
import ServiceTestTypeGuard from './ServiceTestTypeGuard';
import TestDetailPanel from './detail/TestDetailPanel';
import { TestTypeArrayObjectContextProvider } from './detail/hooks/TestTypeArrayObjectContextProvider';
import { DUPLICATE_OF, NEW_STATE, TEST_TYPE_NAME_PARAM } from './ServiceTestsConstants';
import { useNavigate } from '../../../utilities/PgmUtilities';

/**
 * Redirect helper for invalid Create mode URLs.
 * PGM Navigate does NOT resolve relative paths (usePlmPath concatenates
 * directly), so we must use useNavigate() which calls resolvePath() first.
 */
function CreateModeRedirect() {
    const navigate = useNavigate();
    useEffect(() => {
        navigate('../', { replace: true });
    }, [navigate]);
    return null;
}

/**
 * Service Tests Detail/Create/Copy Page
 *
 * Routes:
 * - Create: /plm/{plmId}/service-tests/:type/tests/new       → testId=undefined
 * - Edit:   /plm/{plmId}/service-tests/:type/tests/:_id      → testId=_id
 * - Copy:   /plm/{plmId}/service-tests/:type/tests/new?dupOf=:srcId → testId=undefined, dupOfId=srcId
 * - Create (multi-type): /plm/{plmId}/service-tests/:type/tests/new?name=:typeName
 */
export default function TestsDetailPage() {
    const { type: group, _id } = useParams();
    const [searchParams] = useSearchParams();

    // 'new' is a literal route segment for create mode — pass undefined to InfrastructureDetailBase
    // so it recognizes isCreateAction=true. Do NOT guard against empty _id.
    const testId = _id === NEW_STATE ? undefined : _id;
    const dupOfId = searchParams.get(DUPLICATE_OF) || undefined;
    const typeName = searchParams.get(TEST_TYPE_NAME_PARAM) || undefined;

    if (isEmpty(group)) {
        return (
            <TestTypeArrayObjectContextProvider testTypeArrayObject={[]}>
                <TestDetailPanel
                    key={testId || (dupOfId ? `new-copy-${dupOfId}` : `new${typeName ? `-${typeName}` : ''}`)}
                    testId={testId}
                    dupOfId={dupOfId}
                    groupExist={false}
                />
            </TestTypeArrayObjectContextProvider>
        );
    }

    return (
        <ServiceTestTypeGuard group={group}>
            {(testTypeArrayObject) => {
                // Redirect: multi-type Create without valid ?name= → back to list.
                // Copy mode (dupOfId set) is excluded — copy gets its type from source test data.
                const isCreateMode = !testId && !dupOfId;
                const isMultiType = testTypeArrayObject.length > 1;

                if (isCreateMode && isMultiType) {
                    const hasValidTypeName = typeName && testTypeArrayObject.some((t) => t.name === typeName);
                    if (!hasValidTypeName) {
                        return <CreateModeRedirect />;
                    }
                }

                // Resolve test type ID synchronously so TestDetailPanel's first render
                // uses the correct type. This avoids mounting wrong form sections,
                // triggering wrong API calls, or registering wrong RHF fields.
                const initialTestTypeId =
                    isCreateMode && typeName ? testTypeArrayObject.find((t) => t.name === typeName)?._id ?? null : null;

                return (
                    <TestTypeArrayObjectContextProvider testTypeArrayObject={testTypeArrayObject}>
                        {/* Force React to unmount/remount TestDetailPanel when navigating between tests.
                            Without a key, React reuses the component instance and stale form state persists.
                            Equivalent to portal/ui's $state.go({reload: true}). */}
                        <TestDetailPanel
                            key={testId || (dupOfId ? `new-copy-${dupOfId}` : `new${typeName ? `-${typeName}` : ''}`)}
                            testId={testId}
                            dupOfId={dupOfId}
                            initialTestTypeId={initialTestTypeId}
                            groupExist
                        />
                    </TestTypeArrayObjectContextProvider>
                );
            }}
        </ServiceTestTypeGuard>
    );
}
